<template>
  <div id="employeeEdit">

    <PageTitle :title="employeeName || $t('employees.new')" />

    <PageContainer :title="$t('employees.configuration')">
      <EmployeeForm v-if="employee" v-model="employee" :branch-id="branchId" :key="formKey" :validation="$validate"/>
    </PageContainer>

    <PageFooter :backAction="{ action: back, text: $t('back') }"
                :mainAction="$store.getters.loggedUserHasModifyPermission ? { action: save, text: $t('save') } : undefined"/>

  </div>
</template>

<script>
  import EmployeeForm from "@/components/qsr/EmployeeForm";
  import PageTitle from "@/components/PageTitle";
  import PageContainer from "@/components/PageContainer";
  import PageFooter from "@/components/PageFooter";
  import {Core} from "@/core/Core";

  export default {
    name: "employeeEdit",

    components: { EmployeeForm, PageTitle, PageContainer, PageFooter },

    data() {
      return {
        employee: null,
        formKey: 0
      }
    },

    computed: {
      employeeId() {
        return this.$route.query.employee;
      },
      employeeName() {
        return this.employee && (this.employee.firstName || this.employee.lastName)
            ? (this.employee.firstName + ' ' + this.employee.lastName)
            : null;
      },
      companyId() {
        return this.$route.query.company;
      },
      branchId() {
        return this.$route.query.branch;
      },
      contextLanguage() {
        return this.$ts.state.locale;
      }
    },

    async mounted() {
      await this.loadEmployee(this.employeeId);
    },

    validate: {
      "employee.firstName": {
        required: true
      },
      "employee.lastName": {
        required: true
      }
    },

    methods: {
      async loadEmployee(employeeID) {
        if (employeeID) {
          showSpinner();
          let response = await Core.noThrowRequest('GET', `/api/employees/${employeeID}?augment=embed`);
          this.setEmployee(response)
          hideSpinner();
        } else {
          this.employee = {
            id: null,
            branch: this.branchId,
            firstName: "",
            lastName: "",
            password: "",
            preferences: {
              publicName: undefined,
              language: this.contextLanguage,
              leftHanded: false
            },
            companies: this.companyId
                ? [{id: this.companyId, pin: '', posRole: { id: null }, revenueCenter: { id: null }}]
                : [],
          };
        }
      },

      async findTakenPins() {
        showSpinner();
        let response = await Core.noThrowRequest('POST', '/api/pin_availability', {
          excludeEmployee: this.employee.id || undefined,
          check: this.employee.companies.map(c => ({company: c.id, pin: c.pin}))
        });
        hideSpinner();

        return response.taken || [];
      },

      async save() {
        if (!this.$validate.valid()) {
          toast(this.$t("invalid_or_missing_fields"), "error", this.$t("error.title"));
          this.$validate.validateAllOnce();
          this.formKey++;//this is to force re-rendering of the employeeForm for the validation
          return;
        }
        for (let company of this.employee.companies) {
          if (!company.posRole.id) {
            toast(this.$t("employees.employee_role_missing"), "error", this.$t("error.title"));
            return;
          }
        }

        let takenPins = await this.findTakenPins();
        if (takenPins.length > 0) {
          toast(`PIN taken: ${takenPins.join(', ')}`, "error", this.$t("error.title"));
          return;
        }

        showSpinner();
        let data = JSON.parse(JSON.stringify(this.employee));
        let isNewEmployee = !data.id;
        let response = isNewEmployee
            ? await Core.noThrowRequest('POST', `/api/employees?augment=embed`, data)
            : await Core.noThrowRequest('PUT',  `/api/employees/${data.id}?augment=embed`, data);
        hideSpinner();

        if (response) {
          toast(this.$t("employees.save_success"), "success", this.$t("success"));
          if (isNewEmployee) {
            await this.$router.push(this.$router.buildContext("/employee/edit", {employee: response.id}));
          } else {
            this.setEmployee(response)
          }
        }
      },

      setEmployee(employeeData) {
        delete employeeData["passwordHash"]
        this.employee = employeeData
      },

      back() {
        this.$router.push(this.$router.buildContext("/employees"));
      }
    }
  }
</script>

<style lang="scss" scoped>
  .filler {
    flex-grow: 1;
  }
</style>